import {
    STANDARD_SYMBOL_MAPPING,
    STANDARD_SYMBOL_VARIANT_ASSET_PATHS,
} from "@buildwithflux/constants/standardSymbolPreviews";

import {getAssetPipelineCdnBaseUrl, isAssetPipelinePreviewUrl} from "./assetPipeline";

export const Processed3dModelViews = ["iso", "top", "bottom", "front", "back", "left", "right"] as const;
export type Processed3dModelView = (typeof Processed3dModelViews)[number];

export interface Processed3dModelPreviewOptions {
    view?: Processed3dModelView;
    size?: string;
    format?: "png" | "webp";
    cadMode?: boolean;
    yaw?: number;
    pitch?: number;
    up?: string;
    zIsUp?: boolean;
    zoom?: number;
    ortho?: boolean;
    fov?: number;
    bg?: string;
    focusX?: number;
    focusY?: number;
}

export type PartThumbnailModel = {storageName: string; zIsUp?: boolean};

export const PART_MODEL_THUMBNAIL_OPTIONS = {
    view: "iso",
    yaw: -45,
    pitch: 35,
} as const satisfies Processed3dModelPreviewOptions;

export function isProcessed3dModelPreviewUrl(value: string): boolean {
    return isAssetPipelinePreviewUrl(value);
}

export function getProcessed3dModelPreviewUrl(
    key: string,
    {
        view = "iso",
        size = "512x512",
        format = "webp",
        cadMode = false,
        yaw,
        pitch,
        up,
        zIsUp,
        zoom,
        ortho,
        fov,
        bg,
        focusX,
        focusY,
    }: Processed3dModelPreviewOptions = {},
): string {
    let url = `${getAssetPipelineCdnBaseUrl()}/?key=${encodeURIComponent(
        key,
    )}&format=${format}&view=${view}&size=${encodeURIComponent(size)}`;
    if (cadMode) url += "&cadMode=true";
    if (yaw !== undefined) url += `&yaw=${yaw}`;
    if (pitch !== undefined) url += `&pitch=${pitch}`;
    if (up !== undefined) url += `&up=${up}`;
    if (zIsUp === false) url += "&zIsUp=false";
    if (zoom !== undefined) url += `&zoom=${zoom}`;
    if (ortho !== undefined) url += `&ortho=${ortho}`;
    if (fov !== undefined) url += `&fov=${fov}`;
    if (bg !== undefined) url += `&bg=${encodeURIComponent(bg)}`;
    if (typeof focusX === "number" && typeof focusY === "number") {
        url += `&focusX=${focusX}&focusY=${focusY}`;
    }
    return url;
}

export function getPartThumbnailModelFromSearchHit(hit: unknown): PartThumbnailModel | undefined {
    if (!hit || typeof hit !== "object") {
        return undefined;
    }

    const searchHit = hit as {three_d_model_key?: unknown; three_d_model_z_is_up?: unknown};
    if (typeof searchHit.three_d_model_key !== "string" || !searchHit.three_d_model_key) {
        return undefined;
    }

    return {
        storageName: searchHit.three_d_model_key,
        ...(typeof searchHit.three_d_model_z_is_up === "boolean" ? {zIsUp: searchHit.three_d_model_z_is_up} : {}),
    };
}

const standardSymbolAssetPaths = new Set([
    ...Object.values(STANDARD_SYMBOL_MAPPING),
    ...Object.values(STANDARD_SYMBOL_VARIANT_ASSET_PATHS),
]);

function isAutomaticDefaultPreview(preview: string | undefined): boolean {
    return !preview || standardSymbolAssetPaths.has(preview) || isProcessed3dModelPreviewUrl(preview);
}

export function getPartDefaultPreviewImage(
    previewImage: string | undefined,
    model: PartThumbnailModel | undefined,
): string | undefined {
    if (previewImage && !isAutomaticDefaultPreview(previewImage)) {
        return previewImage;
    }

    if (model) {
        return getProcessed3dModelPreviewUrl(model.storageName, {
            ...PART_MODEL_THUMBNAIL_OPTIONS,
            zIsUp: model.zIsUp,
        });
    }

    if (previewImage && isProcessed3dModelPreviewUrl(previewImage)) {
        return undefined;
    }

    return previewImage || undefined;
}
